/**
 * F1 -- say what the thing IS, where the reader meets it.
 *
 * "Concept taught" scored zero on every step for every persona. This
 * is the smallest thing that fixes it: one or two plain sentences at
 * the point of use, and a way to the fuller definition.
 *
 * Modelled on GroundingNote -- a quiet line that belongs to the
 * surface rather than a modal that interrupts it. A reader who already
 * knows the term should be able to ignore it without effort, which is
 * why it is small, grey, and never demands a dismissal.
 */
import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { QueryClient, QueryClientContext, useQuery } from "@tanstack/react-query";

import { api } from "shared/api/client";
import { PreferencesUnreachable } from "shared/api/preferencesReach";
import type { UserPreferences } from "shared/api/types";
import { userPreferencesKeys } from "shared/api/queryClient";
import { GLOSSARY } from "shared/glossary/terms";
import { recordActedOn, recordShown, shownBefore } from "shared/glossary/teachOnce";

/* Round 17 G21 -- teach once. The explainer showed at every meeting
   (the cascade and positions for the administrator, four boxes on the
   tolerance form, Positions on every My Work visit). It now reads the
   same per-person record as FirstMeetings: once the reader says they
   have it, the line goes, and the term stays in the glossary and in
   every InfoTip. Until the record has loaded, or where it cannot be
   read (a seat confined to its own surface), the line shows. */
const FALLBACK_CLIENT = new QueryClient({
  defaultOptions: { queries: { retry: false } },
});

function useMetTerms(): { met: Set<string>; loaded: boolean; client: QueryClient } {
  const client = useContext(QueryClientContext) ?? FALLBACK_CLIENT;
  const unreachable = useContext(PreferencesUnreachable);
  const prefs = useQuery(
    {
      queryKey: userPreferencesKeys.all,
      queryFn: () => api.get<UserPreferences>("/v1/me/preferences"),
      staleTime: 5 * 60_000,
      retry: false,
      enabled: !unreachable,
    },
    client,
  );
  const terms = prefs.data?.met_terms;
  return {
    met: new Set(Array.isArray(terms) ? terms : []),
    loaded: Array.isArray(terms),
    client,
  };
}

export function Explainer({
  termKey,
  className,
  inline = false,
}: {
  /** A key in the glossary. An unknown key renders nothing rather
   *  than an empty box -- a missing definition is a gap to fill, not
   *  a hole to show the reader. */
  termKey: string;
  className?: string;
  /**
   * E5 -- open the fuller definition IN PLACE instead of routing to
   * /glossary.
   *
   * For a surface the shell renders on every route -- the examiner
   * workspace is returned for any path once the session holds that
   * role -- a link to /glossary re-renders the surface the reader is
   * already on. It looks like a broken link and it is really a
   * routing assumption that does not hold there. Opening inline
   * removes the assumption rather than working around it.
   */
  inline?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [gotHere, setGotHere] = useState(false);
  const { met, loaded, client } = useMetTerms();
  const term = GLOSSARY[termKey];
  // Round 18 G29 -- the surface that first shows a term owns this
  // meeting and keeps showing it; any later one is quiet.
  const [ownsMeeting] = useState(() => !shownBefore(termKey));
  const showing = Boolean(term) && !gotHere && !met.has(termKey) && ownsMeeting;
  useEffect(() => {
    if (showing && loaded) recordShown(termKey);
  }, [showing, loaded, termKey]);
  if (!term) return null;
  if (gotHere || met.has(termKey) || !ownsMeeting) {
    /* Round 19 H54 -- the line goes quiet; its door to the glossary
       does not. Once a term was stored the portfolio and the admission
       page offered no way to its definition, and the Resilience Lead
       typed the glossary's address to find one. */
    return inline ? null : (
      <p className={`text-xs ${className ?? ""}`.trim()}>
        <Link
          to={`/glossary#${termKey}`}
          data-testid={`explainer-door-${termKey}`}
          className="text-brand-brassDark hover:underline"
        >
          {t("glossary.learnMoreAbout", { term: term.term })}
        </Link>
      </p>
    );
  }
  return (
    <div
      data-testid={`explainer-${termKey}`}
      /* C4 (round 8) -- a paragraph takes its direction from its own
         first strong character, not from the page.
         Until the glossary is translated these definitions are English
         prose inside an RTL document, so the full stop that ends each
         sentence is a NEUTRAL taking the paragraph's direction: it was
         rendering at the LEFT of the line, ahead of the sentence it
         closes, on every definition a newcomer meets. `dir="auto"`
         resolves each paragraph from its own content, so an English
         definition reads left-to-right today and a translated one
         reads right-to-left the day it lands, with nothing to change. */
      dir="auto"
      className={`text-xs text-slate-500 ${className ?? ""}`.trim()}
    >
      <p>
        <span className="font-medium text-slate-600">{term.term}:</span> {term.short}{" "}
        {inline ? (
          <button
            type="button"
            data-testid={`explainer-more-${termKey}`}
            aria-expanded={open}
            onClick={() => {
              if (!open) recordActedOn(termKey);
              setOpen((v) => !v);
            }}
            className="text-brand-brassDark hover:underline"
          >
            {open ? t("glossary.showLess") : t("glossary.learnMore")}
          </button>
        ) : (
          <Link
            to={`/glossary#${termKey}`}
            data-testid={`explainer-more-${termKey}`}
            onClick={() => recordActedOn(termKey)}
            className="text-brand-brassDark hover:underline"
          >
            {t("glossary.learnMore")}
          </Link>
        )}
        {loaded && (
          <>
            {" "}
            <button
              type="button"
              data-testid={`explainer-got-it-${termKey}`}
              onClick={() => {
                setGotHere(true);
                void api
                  .patch<UserPreferences>("/v1/me/preferences", { met_term: termKey })
                  .then((data) => client.setQueryData(userPreferencesKeys.all, data))
                  .catch(() => undefined);
              }}
              className="font-medium text-brand-brassDark hover:underline"
            >
              {t("glossary.firstMeeting.gotIt")}
            </button>
          </>
        )}
      </p>
      {inline && open && (
        <p
          data-testid={`explainer-full-${termKey}`}
          className="mt-1 rounded border border-slate-200 bg-white px-2 py-1.5 text-slate-600"
        >
          {term.more ?? term.short}
        </p>
      )}
    </div>
  );
}

/**
 * The compact variant, for a table column header or a chip label --
 * places where the construct is met but a paragraph would not fit.
 *
 * A native `title` rather than a hover card: it works on keyboard
 * focus, it works for a screen reader, it cannot be clipped by an
 * overflow rule, and it needs no state. The visible affordance is a
 * small marker so a reader knows there is something to hover; the
 * marker is aria-hidden because the description is already on the
 * wrapper.
 */
const TIP_TONE = {
  // The hero and the ops-wall chrome are navy; slate-on-navy is
  // invisible, which would make the affordance a decoration.
  light: "border-slate-300 text-slate-400",
  dark: "border-white/30 text-sky-label",
} as const;

export function InfoTip({
  termKey,
  tone = "light",
  className,
  marker = "?",
}: {
  termKey: string;
  tone?: keyof typeof TIP_TONE;
  className?: string;
  /** Round 20 H59 -- beside a figure, "?" made the label read as a
   *  question ("BUSINESS SERVICES IMPACTED?" over "9 / 9"); "i" marks
   *  the same explanation without asking anything. */
  marker?: "?" | "i";
}) {
  const term = GLOSSARY[termKey];
  if (!term) return null;
  return (
    <span
      data-testid={`infotip-${termKey}`}
      title={`${term.term}: ${term.short}`}
      aria-label={`${term.term}: ${term.short}`}
      tabIndex={0}
      className={`ms-1 cursor-help rounded-full border px-1 text-[9px] font-semibold leading-tight ${TIP_TONE[tone]} ${className ?? ""}`.trim()}
    >
      <span aria-hidden="true">{marker}</span>
    </span>
  );
}
